import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import { apiGet, apiMutate } from '../admin/primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { useMenuContextuel, type GroupeMenu } from '../shell/menu-contextuel.js'
import type { WidgetPlacement } from './registry.js'
import { gestesNatifs } from './menu-tuiles.js'
import { POIGNEE_SECTION, crans, sauf } from './menu-tuiles-modele.js'

/** Le menu d'une section ne s'arme pas sur sa poignée : un appui y lance le glisser. */
const HORS_MENU_SECTION = gestesNatifs(POIGNEE_SECTION)

/**
 * ORDRE DES SECTIONS D'UNE PAGE-PILE.
 *
 * La page Soirée est une GRILLE : deux axes, des tailles, des collisions.
 * La page liste Soirées est une PILE : les sections font toute la largeur,
 * seules leurs positions relatives se règlent. Confondre les deux aurait
 * donné soit une grille inutilisable pour une liste, soit une pile incapable
 * de représenter la page Soirée.
 *
 * Ce qui EST partagé, en revanche : la persistance (`page_layout_default`),
 * la route, la permission, l'audit, la barre d'enregistrement et la garde de
 * sortie. Rien de tout cela n'est réécrit ici.
 *
 * Le serveur renvoie une liste complète et déjà ordonnée — y compris les
 * sections qu'une disposition enregistrée plus ancienne ne connaît pas. Ce
 * fichier ne réordonne donc jamais de sa propre initiative.
 */

export interface SectionDef {
  id: string
  /** Nom montré pendant l'édition — jamais un identifiant technique. */
  label: string
}

export interface SectionOrderState {
  order: string[]
  editing: boolean
  setEditing: (v: boolean) => void
  canEdit: boolean
  loaded: boolean
  move: (id: string, versIndex: number) => void
}

export function useSectionOrder(pageKey: string, sections: SectionDef[]): SectionOrderState {
  const canonique = sections.map((s) => s.id)
  const [order, setOrder] = useState<string[]>(canonique)
  const [snapshot, setSnapshot] = useState<string[] | null>(null)
  const [editing, setEditing] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let vivant = true
    apiGet<{ widgets: WidgetPlacement[] | null; can_edit?: boolean }>(
      `/api/v1/page-layout/${pageKey}`)
      .then((r) => {
        if (!vivant) return
        // Le serveur a déjà fusionné avec le registre canonique ; on ne
        // garde ici que les sections que CET écran sait rendre.
        const connues = new Set(canonique)
        const recu = (r.widgets ?? []).map((w) => w.id).filter((id) => connues.has(id))
        const o = recu.length > 0 ? recu : canonique
        setOrder(o); setSnapshot([...o])
        setCanEdit(r.can_edit === true)
        setLoaded(true)
      })
      .catch(() => {
        if (!vivant) return
        setOrder(canonique); setSnapshot([...canonique]); setLoaded(true)
      })
    return () => { vivant = false }
  }, [pageKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const changed = snapshot !== null && order.join('|') !== snapshot.join('|')

  useSaveBar({
    // Même doctrine que la grille : la barre est là PENDANT tout le mode
    // édition, pas seulement après le premier déplacement. Sinon on entre en
    // édition sans aucun moyen visible d'en sortir.
    dirty: editing,
    ...(changed ? { count: 1 } : {}),
    saveLabel: 'Enregistrer l’ordre',
    onSave: async () => {
      const widgets = order.map((id, i) => ({
        id, x: 0, y: i, w: 12, h: 1, visibility: 'ALWAYS' as const,
      }))
      const r = await apiMutate<{ ok?: boolean; message?: string }>(
        'PUT', `/api/v1/page-layout/${pageKey}`, { widgets })
      if (r.status !== 200) return r.data.message ?? 'Ordre refusé par le serveur.'
      setSnapshot([...order])
      setEditing(false)
      return true
    },
    onCancel: () => {
      if (snapshot) setOrder([...snapshot])
      setEditing(false)
    },
    successMessage: 'Ordre enregistré — c’est désormais celui de la page pour tout le monde.',
  })

  const move = useCallback((id: string, versIndex: number) => {
    setOrder((prev) => {
      const depuis = prev.indexOf(id)
      if (depuis < 0) return prev
      const cible = Math.max(0, Math.min(prev.length - 1, versIndex))
      if (cible === depuis) return prev
      const suivant = [...prev]
      suivant.splice(depuis, 1)
      suivant.splice(cible, 0, id)
      return suivant
    })
  }, [])

  return { order, editing, setEditing, canEdit, loaded, move }
}

/**
 * Une section réordonnable.
 *
 * En consultation, ce composant est TRANSPARENT : il rend ses enfants et
 * rien d'autre. Un écran qui garderait des poignées, des bordures ou une
 * grille de fond hors édition paierait en permanence le prix d'une
 * fonctionnalité utilisée trois fois par an.
 */
export function ReorderableSection({ state, id, label, children }: {
  state: SectionOrderState
  id: string
  label: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [saisi, setSaisi] = useState(false)
  const menu = useMenuContextuel()

  if (!state.editing) return <>{children}</>

  const index = state.order.indexOf(id)

  /*
   * DÉPLACEMENT AU POINTEUR — la section suit le doigt et permute dès
   * qu'elle dépasse le MILIEU d'une voisine. Permuter au premier pixel de
   * chevauchement faisait osciller la liste entre deux positions ; le
   * milieu donne un seuil stable et prévisible.
   */
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    e.preventDefault()
    const el = ref.current
    if (!el) return
    const departY = e.clientY
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    setSaisi(true)

    const bouger = (ev: PointerEvent) => {
      const dy = ev.clientY - departY
      el.style.transform = `translateY(${dy}px)`
      const voisines = [...document.querySelectorAll<HTMLElement>('[data-section-id]')]
        .filter((n) => n !== el)
      const moi = el.getBoundingClientRect()
      const centre = moi.top + moi.height / 2
      for (const n of voisines) {
        const r = n.getBoundingClientRect()
        if (centre > r.top && centre < r.bottom) {
          const cible = state.order.indexOf(n.dataset['sectionId'] ?? '')
          if (cible >= 0) {
            el.style.transform = ''
            state.move(id, cible)
          }
          break
        }
      }
    }
    const finir = () => {
      el.style.transform = ''
      setSaisi(false)
      window.removeEventListener('pointermove', bouger)
      window.removeEventListener('pointerup', finir)
    }
    window.addEventListener('pointermove', bouger)
    window.addEventListener('pointerup', finir)
  }

  /* Le clavier n'est pas un repli dégradé : c'est le SEUL moyen pour qui
     n'utilise pas de souris. Mêmes déplacements, même état. */
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); state.move(id, index - 1) }
    if (e.key === 'ArrowDown') { e.preventDefault(); state.move(id, index + 1) }
  }

  /* MENU CLIC DROIT (24/10/2026) — les deux flèches de la poignée, en mots :
     mêmes déplacements, même brouillon, la barre d'enregistrement confirme.
     En édition seulement (hors édition, ce composant ne rend rien à lui), et
     grisées là où la flèche ne ferait rien. */
  const actionsSection = (): GroupeMenu[] => {
    const c = crans(state.order, id)
    return [{ actions: [
      { id: 'monter', libelle: 'Monter d’un cran', icone: <ArrowUp size={15} />, agir: () => state.move(id, index - 1),
        ...(c.monter ? {} : { indisponible: 'Déjà en haut de la page' }) },
      { id: 'descendre', libelle: 'Descendre d’un cran', icone: <ArrowDown size={15} />, agir: () => state.move(id, index + 1),
        ...(c.descendre ? {} : { indisponible: 'Déjà en bas de la page' }) },
    ] }]
  }

  return (
    <div ref={ref} className={`so-reorder${saisi ? ' so-reorder--held' : ''}`}
      data-section-id={id}
      {...(state.canEdit ? sauf(menu(actionsSection, label), HORS_MENU_SECTION) : {})}>
      <div className="so-reorder__bar">
        <button type="button" className="so-reorder__grip"
          onPointerDown={onPointerDown} onKeyDown={onKey}
          aria-label={`Déplacer la section « ${label} ». Flèches haut et bas pour changer sa place.`}>
          <GripVertical size={15} aria-hidden="true" />
        </button>
        <span className="so-reorder__label">{label}</span>
        <span className="so-reorder__rank">{index + 1} / {state.order.length}</span>
      </div>
      <div className="so-reorder__content" aria-hidden={saisi}>{children}</div>
    </div>
  )
}
